import { createSlice } from '@reduxjs/toolkit'

import { fetchUser } from 'src/modules/auth/slice'
import api, { createWrappedAsyncThunk } from 'src/api'
import axios from 'axios'
import config from '../../../../config'
import { Auth } from 'aws-amplify'

export const initialState = {
  draftOrder: null,
  draftOrderList: [],
  scheduledOrderList: [],
  repeatOrderSchedule: null,
  order: null,
  orderList: [],
  purchasingEnvironment: undefined,
  purchasingEnvironments: [],
  products: [],
}

const SLICE_NAME = 'buyer/purchasing'

const getJwt = async () => {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

const v2 = {
  get: async url => {
    try {
      const jwt = await getJwt()
      if (jwt) {
        const result = await axios.get(`${config.api.v2endpoint}${url}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
        })
        return result.data
      }
    } catch (e) {
      return e
    }
  },
  post: async (url, body) => {
    try {
      const jwt = await getJwt()
      console.info('initiating post request to: ', config.api.v2endpoint, url)
      if (jwt) {
        const result = await axios.post(
          `${config.api.v2endpoint}${url}`,
          body,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
          }
        )
        return result.data
      }
    } catch (e) {
      return e
    }
  },
  put: async (url, body) => {
    try {
      const jwt = await getJwt()
      if (jwt) {
        const result = await axios.put(
          `${config.api.v2endpoint}${url}`,
          { ...body },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
          }
        )
        return result.data
      }
    } catch (e) {
      return e
    }
  },
}

// Products
export const fetchProducts = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchProducts`,
  arg => api.get(`/buyer/environments/${arg}/products/`)
)

// Product actions
export const favouriteProduct = createWrappedAsyncThunk(
  `${SLICE_NAME}/favouriteProduct`,
  ({ id, environmentId }) =>
    api.post(`/buyer/products/${id}/favourite/`, { body: { environmentId } })
)

export const unfavouriteProduct = createWrappedAsyncThunk(
  `${SLICE_NAME}/unfavouriteProduct`,
  ({ id, environmentId }) =>
    api.post(`/buyer/products/${id}/unfavourite/`, { body: { environmentId } })
)

// Draft
export const fetchDraftOrderList = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchDraftOrderList`,
  environmentId => api.get(`/buyer/environments/${environmentId}/draft_orders/`)
)

// export const createDraftOrder = createWrappedAsyncThunk(
//   `${SLICE_NAME}/createDraftOrder`,
//   body => api.post('/buyer/draft_orders/', { body })
// )

export const fetchScheduledOrderList = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchScheduledOrderList`,
  environmentId => v2.get(`/buyer/environments/${environmentId}/scheduled`)
)

export const createRepeatOrderSchedule = createWrappedAsyncThunk(
  `${SLICE_NAME}/createRepeatOrderSchedule`,
  ({ draftOrderId, schedule }) => {
    return v2.post(`/buyer/draft_orders/${draftOrderId}/repeat_orders`, {
      body: schedule,
    })
  }
)

export const updateRepeatOrderSchedule = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateRepeatOrderSchedule`,
  ({ id, draftOrderId, ...body }) =>
    v2.put(`/buyer/draft_orders/${draftOrderId}/repeat_orders/${id}`, {
      ...body,
    })
)

export const updateScheduledOrderStatus = createWrappedAsyncThunk(
  `${SLICE_NAME}/pauseScheduledOrder`,
  ({ id, draftOrderId, status }) =>
    v2.put(`/buyer/draft_orders/${draftOrderId}/repeat_orders/${id}/status`, {
      status,
    })
)

export const createDraftOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/createDraftOrder`,
  body => v2.post('/buyer/draft_orders', { body })
)

// v1
// export const fetchDraftOrder = createWrappedAsyncThunk(
//   `${SLICE_NAME}/fetchDraftOrder`,
//   async ({ id, environmentId }) => {
//     api.get(`/buyer/draft_orders/${id}/`, {
//       queryStringParameters: { environmentId },
//     })
//   }
// )

export const fetchDraftOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchDraftOrder`,
  async ({ id, environmentId }) =>
    v2.get(`/buyer/draft_orders/${id}?environment_id=${environmentId}`)
)

export const deleteDraftOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/deleteDraftOrder`,
  id => api.del(`/buyer/draft_orders/${id}/`)
)

export const editDraftOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/editDraftOrder`,
  ({ id, ...body }) => api.put(`/buyer/draft_orders/${id}/`, { body })
)

export const submitDraftOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/submitDraftOrder`,
  id => api.post(`/buyer/draft_orders/${id}/submit/`)
)

// export const submitDraftOrder = createWrappedAsyncThunk(
//   `${SLICE_NAME}/submitDraftOrder`,
//   id => v2.post(`/buyer/draft_orders/${id}/submit/`)
// )

// Orders
export const fetchOrderList = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchOrderList`,
  environmentId => api.get(`/buyer/environments/${environmentId}/orders/`)
)

export const fetchOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchOrder`,
  id => api.get(`/buyer/orders/${id}/`)
)

export const submitOrderReceivedOk = createWrappedAsyncThunk(
  `${SLICE_NAME}/markApproved`,
  id => api.post(`/buyer/orders/${id}/mark_approved/`, {})
)

export const submitOrderReceivedOkAfterDispute = createWrappedAsyncThunk(
  `${SLICE_NAME}/markApproved`,
  id => api.post(`/buyer/orders/${id}/mark_approved_after_dispute/`, {})
)

export const submitDisputeWithOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/markDisputed`,
  id => api.post(`/buyer/orders/${id}/mark_disputed/`, {})
)

export const submitDisputeResolvedForOrder = createWrappedAsyncThunk(
  `${SLICE_NAME}/markDisputed`,
  id => api.post(`/buyer/orders/${id}/remove_dispute/`, {})
)

export const editDisputeReasonForLineItem = createWrappedAsyncThunk(
  `${SLICE_NAME}/editLineItemDisputeReason`,
  ({ id, data }) => {
    api.put(`/buyer/line-items/${id}/`, { body: data })
  }
)

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setPurchasingEnvironment(state, { payload }) {
      state.purchasingEnvironment = payload
      return state
    },
  },
  extraReducers: {
    [fetchUser.fulfilled]: (state, { payload }) => {
      if (payload.organisation.type === 'buyer') {
        state.purchasingEnvironments = payload.environments
        // check purchasingEnvironment stored is valid, ie could be bad one
        // from localStorage
        const invalidEnvironment = payload.environments.every(
          p => p.id !== state.purchasingEnvironment
        )
        if (invalidEnvironment && payload.environments.length) {
          state.purchasingEnvironment = payload.environments[0].id
        }
      }
      return state
    },
    // Create order
    [fetchProducts.fulfilled]: (state, { meta, payload }) => {
      state.products = payload
      return state
    },
    [favouriteProduct.fulfilled]: (state, { meta, payload }) => {
      const agreementProduct = state.products.find(
        p => p.product.id === meta.arg.id
      )
      agreementProduct.product.favourite = true
      return state
    },
    [unfavouriteProduct.fulfilled]: (state, { meta, payload }) => {
      const agreementProduct = state.products.find(
        p => p.product.id === meta.arg.id
      )
      agreementProduct.product.favourite = false
      return state
    },
    // Drafts
    [fetchDraftOrderList.fulfilled]: (state, { payload }) => {
      state.draftOrderList = payload
      return state
    },
    [fetchScheduledOrderList.fulfilled]: (state, { payload }) => {
      state.scheduledOrderList = payload
      return state
    },
    [createDraftOrder.fulfilled]: (state, { payload }) => {
      state.draftOrder = payload
      return state
    },
    [createRepeatOrderSchedule.fulfilled]: (state, { payload }) => {
      state.repeatOrderSchedule = payload
      return state
    },
    [fetchDraftOrder.fulfilled]: (state, { payload }) => {
      state.draftOrder = payload
      return state
    },
    // Orders
    [fetchOrderList.fulfilled]: (state, { payload }) => {
      state.orderList = payload
      return state
    },
    [fetchOrder.fulfilled]: (state, { payload }) => {
      state.order = payload
      return state
    },
  },
})

export const { setPurchasingEnvironment } = slice.actions

export default slice.reducer
